import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FrontBanner from "@/assets/images/front-banner.svg";
import { ROUTESLIST } from "@/static/urls";
import { MEMBERSHIP_API_URL } from "@/static/api";
import { membershipPackages } from "@/utils/constants";
import "./index.scss";

const TIER_TO_PACKAGE_ID = { pro: "ample-samples-pro", lite: "ample-samples-lite" };

const MembershipCheckout = () => {
  const location = useLocation();
  const history = useHistory();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tier = new URLSearchParams(location.search).get("tier")?.toLowerCase();
  const packageId = tier ? TIER_TO_PACKAGE_ID[tier] : null;

  useEffect(() => {
    if (!packageId) {
      history.replace(ROUTESLIST.PACKAGES);
      return;
    }
    const found = membershipPackages.find((p) => p.id === packageId);
    setPkg(found || null);
    if (!found) history.replace(ROUTESLIST.PACKAGES);
  }, [packageId, history]);

  const handleContinueToPayment = async () => {
    if (!pkg || !tier) return;
    setLoading(true);
    setError(null);
    const origin = window.location.origin;
    const successUrl = `${origin}${ROUTESLIST.CHECKOUT_MEMBERSHIP_SUCCESS}?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${origin}${ROUTESLIST.CHECKOUT_MEMBERSHIP}?tier=${tier}`;

    try {
      const res = await fetch(`${MEMBERSHIP_API_URL}/create-membership-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
        redirect: "manual",
      });
      if (res.type === "opaqueredirect" || (res.status === 0 && !res.ok)) {
        setError(
          "Checkout API returned a redirect. Ensure the API at " +
            MEMBERSHIP_API_URL +
            " returns JSON (no redirect to login or frontend) and allows CORS for this origin."
        );
        setLoading(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || data?.error || "Could not start checkout. Please try again.");
        setLoading(false);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Invalid response from server. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      if (err?.message?.includes("CORS") || err?.message?.includes("Failed to fetch")) {
        const origin = typeof window !== "undefined" ? window.location.origin : "this app";
        setError(
          "Cannot reach checkout API. Call: POST " +
            MEMBERSHIP_API_URL +
            "/create-membership-checkout-session. Ensure that URL is running and the server allows CORS for origin: " +
            origin
        );
      } else {
        setError(err?.message || "Could not start checkout. Please try again.");
      }
      setLoading(false);
    }
  };

  if (!pkg) return null;

  return (
    <main className="page page-shop membership-checkout-page">
      {loading && (
        <div className="membership-checkout-overlay">
          <div className="membership-checkout-spinner" />
          <span className="membership-checkout-overlay-text">Redirecting to payment…</span>
        </div>
      )}
      <div className="product-section">
        <div className="product-header" style={{ justifyContent: "space-between" }}>
          <div
            className="back-to-home-section"
            onClick={() => history.push(ROUTESLIST.PACKAGES)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && history.push(ROUTESLIST.PACKAGES)}
          >
            <ArrowBackIcon />
          </div>
          <div className="back-to-home-section-image">
            <img src="https://ample-samples.s3.us-east-2.amazonaws.com/app/front-banner.svg" alt="Ample Samples" />
          </div>
          <div>&nbsp;</div>
        </div>

        <div className="subscription-section">
          <div className="subscription-section-center">
            <h2 className="membership-checkout-title">Membership checkout</h2>
            <p className="membership-checkout-subtitle">No account needed – pay first, we’ll create your account after payment.</p>

            <div className="membership-checkout-card">
              <div className="membership-checkout-card__grid">
                <div className="membership-checkout-card__plan">
                  <div className="membership-checkout-card__plan-header">
                    <h3 className="membership-checkout-card__plan-name">{pkg.name}</h3>
                    {pkg.badge && <span className="membership-checkout-card__badge">⭐ {pkg.badge}</span>}
                  </div>
                  <div className="membership-checkout-card__plan-price">
                    <span className="membership-checkout-card__amount">${pkg.price}</span>
                    <span className="membership-checkout-card__currency">USD</span>
                  </div>
                  <p className="membership-checkout-card__description">{pkg.description}</p>
                  {pkg.features && pkg.features.length > 0 && (
                    <ul className="membership-checkout-card__features">
                      {pkg.features.map((feature, id) => (
                        <li key={id} dangerouslySetInnerHTML={{ __html: feature.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
                      ))}
                    </ul>
                  )}
                </div>

                <div className="membership-checkout-card__summary">
                  <h3 className="membership-checkout-card__summary-title">Summary</h3>
                  <div className="membership-checkout-card__summary-row">
                    <span>1× {pkg.name}</span>
                    <span>${pkg.price}</span>
                  </div>
                  <div className="membership-checkout-card__divider" />
                  <div className="membership-checkout-card__total">
                    <span>Total</span>
                    <strong>${pkg.price} USD</strong>
                  </div>
                </div>
              </div>

              {error && <p className="membership-checkout-error">{error}</p>}

              <button
                type="button"
                className="membership-checkout-btn"
                disabled={loading}
                onClick={handleContinueToPayment}
              >
                {loading ? "Redirecting to payment…" : "Continue to payment"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MembershipCheckout;
